import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DollarSign, Users, TrendingUp, CheckCircle, ArrowRight,
  Zap, Globe, BarChart3, Shield, Mail, ExternalLink, CreditCard,
} from "lucide-react";
import { motion } from "framer-motion";

const CALENDAR_LINK = "https://calendar.app.google/gbZSzz4TiFAYYSjL8";

export default function AffiliatesPage() {
  document.title = "ROAS-AI Affiliate Program | Earn 20% Recurring Commissions";

  const benefits = [
    { icon: DollarSign, title: "20% Recurring Commission", desc: "Earn 20% on every payment your referrals make — for up to 12 months" },
    { icon: TrendingUp, title: "High-Ticket Payouts", desc: "$179.80/mo or $1,998/yr per customer you refer on our Individual plans" },
    { icon: CreditCard, title: "Automatic Stripe Payouts", desc: "Commissions paid directly to your bank account via Stripe Connect" },
    { icon: Users, title: "90-Day Cookie Window", desc: "Long attribution window means more conversions from your audience" },
    { icon: BarChart3, title: "Real-Time Dashboard", desc: "Track clicks, conversions, and earnings in your affiliate dashboard" },
    { icon: Shield, title: "Trusted Brand", desc: "Promote a SOC2-compliant platform trusted by 50+ marketing teams" },
  ];

  const tiers = [
    { plan: "Individual Monthly", price: "$899/mo", commission: "$179.80/mo", annual: "$2,157.60/yr" },
    { plan: "Individual Annual", price: "$9,990/yr", commission: "$1,998/yr", annual: "$1,998/yr" },
    { plan: "Enterprise", price: "Custom", commission: "20% of deal", annual: "Varies" },
  ];

  const steps = [
    { num: "1", title: "Apply & Get Approved", desc: "Sign up in seconds — approved within 24 hours" },
    { num: "2", title: "Connect Stripe", desc: "Link your bank account via Stripe for automatic payouts" },
    { num: "3", title: "Share & Earn", desc: "Share your unique referral link and earn 20% recurring for 12 months" },
  ];

  const faqs = [
    { q: "How much can I earn?", a: "You earn 10% recurring on every payment your referrals make for up to 12 months. On our Monthly plan ($899/mo), that's $89.90/month per customer. On Annual ($9,990/yr), that's $999 per year." },
    { q: "When and how do I get paid?", a: "Payouts are processed via Stripe Connect directly to your bank account. Once your Stripe account is connected and verified, commissions are transferred when the admin processes payouts." },
    { q: "How long is the cookie duration?", a: "90 days. If someone clicks your link and subscribes within 90 days, you get credit for the referral." },
    { q: "Is there a cap on commissions?", a: "Commissions are recurring for up to 12 months per customer. There is no cap on the number of customers you can refer." },
    { q: "Do I need a Stripe account?", a: "Yes. When you join, you'll connect your bank account via Stripe's secure onboarding. This takes about 2 minutes and ensures fast, automated payouts." },
    { q: "Can I promote ROAS-AI internationally?", a: "Yes. ROAS-AI is available globally and you can earn commissions on referrals from any country." },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="border-b border-white/5 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/login" className="flex items-center gap-2">
            <img src="/roas-ai-logo.png" alt="ROAS-AI" className="w-8 h-8 object-contain" />
            <span className="font-heading text-lg font-bold">ROAS-AI</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/pricing-demo" className="text-zinc-400 hover:text-white text-sm transition-colors">Pricing</a>
            <a href="/affiliates/portal" className="text-zinc-400 hover:text-white text-sm transition-colors">Affiliate Login</a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6">
        {/* Hero */}
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-20 text-center">
          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 mb-4">Affiliate Program — Powered by Stripe</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
            Earn 20% Recurring<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">On Every Referral</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-8">
            Promote ROAS-AI and earn up to $2,000/year per customer with automatic payouts via Stripe Connect.
          </p>
          <a href="/affiliates/portal">
            <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold h-14 rounded-xl px-10 text-base">
              Join the Affiliate Program
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </motion.section>

        {/* How It Works */}
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="py-16">
          <h2 className="text-2xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s) => (
              <div key={s.num} className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-emerald-400 font-bold text-lg">{s.num}</span>
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-zinc-400 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Commission Structure */}
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="py-16">
          <h2 className="text-2xl font-bold text-center mb-4">Commission Structure</h2>
          <p className="text-zinc-400 text-center mb-12">20% recurring on all plans for up to 12 months per customer</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((t) => (
              <div key={t.plan} className="bg-[#121212] border border-white/10 rounded-2xl p-6 hover:border-emerald-500/30 transition-colors">
                <h3 className="text-white font-bold mb-1">{t.plan}</h3>
                <p className="text-zinc-400 text-sm mb-4">Customer pays {t.price}</p>
                <div className="border-t border-white/5 pt-4">
                  <p className="text-emerald-400 text-2xl font-bold">{t.commission}</p>
                  <p className="text-zinc-400 text-xs mt-1">Your earning per customer</p>
                  <p className="text-zinc-500 text-xs mt-1">Up to {t.annual} in year 1</p>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="py-16">
          <h2 className="text-2xl font-bold text-center mb-12">Why Promote ROAS-AI</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="bg-[#121212] border border-white/10 rounded-xl p-5">
                  <Icon className="w-6 h-6 text-emerald-400 mb-3" />
                  <h3 className="text-white font-semibold mb-1">{b.title}</h3>
                  <p className="text-zinc-400 text-sm">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </motion.section>

        {/* Stripe Payout Info */}
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="py-16">
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-8 h-8 text-violet-400" />
              <div>
                <h3 className="text-white font-bold text-lg">Automatic Payouts via Stripe</h3>
                <p className="text-zinc-400 text-sm">No invoicing. No chasing payments.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-white font-bold">Instant Setup</p>
                <p className="text-zinc-400 text-xs mt-1">Connect bank in 2 minutes via Stripe</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-white font-bold">Secure</p>
                <p className="text-zinc-400 text-xs mt-1">PCI-compliant, bank-grade security</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-white font-bold">Global</p>
                <p className="text-zinc-400 text-xs mt-1">Payouts to 40+ countries</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="py-16">
          <h2 className="text-2xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="bg-[#121212] border border-white/10 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-2">{f.q}</h3>
                <p className="text-zinc-400 text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <section className="py-16 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Start Earning?</h2>
          <p className="text-zinc-400 mb-8 max-w-md mx-auto">Join the ROAS-AI affiliate program and earn recurring commissions with automatic Stripe payouts.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="/affiliates/portal">
              <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl px-8">
                Join Now — It's Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href={CALENDAR_LINK} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 font-semibold rounded-xl px-8">
                Talk to Us First
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </section>

        {/* Footer */}
        <div className="py-8 border-t border-white/5 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex gap-4">
            <a href="/privacy" className="hover:text-zinc-400">Privacy Policy</a>
            <a href="/terms" className="hover:text-zinc-400">Terms of Service</a>
            <a href="/about" className="hover:text-zinc-400">About Us</a>
            <a href="/pricing-demo" className="hover:text-zinc-400">Pricing</a>
          </div>
          <span>Copyright (c) 2026 Beruf LLC</span>
        </div>
      </div>
    </div>
  );
}
